import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api from "../../lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Package, Article, ChatCircleText, Buildings, Briefcase, Files, ImageSquare, Tag,
  Question, ArrowsLeftRight, ArrowRight, Warning, CheckCircle, Gauge, Sparkle,
} from "@phosphor-icons/react";

const SAFFRON = "#C2410C";
const SAFFRON_LIGHT = "#FF9933";
const SKY = "#0EA5E9";
const SKY_DEEP = "#0369A1";
const GREY = "#9CA3AF";
const PIE_COLORS = [SAFFRON, SKY, "#F59E0B", "#94A3B8"];

const kpiTiles = [
  { key: "products", label: "Products", icon: Package, suffix: "_active", color: SAFFRON, link: "/admin/products" },
  { key: "blogs", label: "Blogs", icon: Article, suffix: "_published", color: SKY, link: "/admin/blogs" },
  { key: "inquiries_7d", label: "Inquiries · 7d", icon: ChatCircleText, color: SAFFRON, secondaryKey: "inquiries_new", secondaryLabel: "new total", link: "/admin/inquiries" },
  { key: "careers_active", label: "Open roles", icon: Briefcase, color: SKY, secondaryKey: "applications_7d", secondaryLabel: "apps · 7d", link: "/admin/careers" },
  { key: "applications", label: "Applications", icon: Files, color: SAFFRON, link: "/admin/applications" },
  { key: "clients", label: "Clients", icon: Buildings, color: SKY, link: "/admin/clients" },
  { key: "categories", label: "Categories", icon: Tag, color: SAFFRON, link: "/admin/categories" },
  { key: "faqs", label: "FAQs", icon: Question, color: SKY, link: "/admin/faqs" },
];

function shortDate(iso) { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }

export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [inquiries, setInquiries] = useState([]);
  const [apps, setApps] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [tsInq, setTsInq] = useState([]);
  const [tsApp, setTsApp] = useState([]);
  const [topCats, setTopCats] = useState([]);
  const [statusBreakdown, setStatusBreakdown] = useState([]);
  const [seo, setSeo] = useState(null);
  const [attention, setAttention] = useState([]);
  const [health, setHealth] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, i, a, b, ti, ta, cats, sts, rdy, att, h] = await Promise.all([
          api.get("/admin/dashboard/stats"),
          api.get("/admin/dashboard/recent-inquiries"),
          api.get("/admin/dashboard/recent-applications"),
          api.get("/admin/dashboard/recent-blogs"),
          api.get("/admin/dashboard/timeseries", { params: { metric: "inquiries", days: 90 } }),
          api.get("/admin/dashboard/timeseries", { params: { metric: "applications", days: 90 } }),
          api.get("/admin/dashboard/top-categories"),
          api.get("/admin/dashboard/inquiry-status"),
          api.get("/admin/dashboard/seo-readiness"),
          api.get("/admin/dashboard/needs-attention"),
          api.get("/admin/dashboard/health"),
        ]);
        setStats(s.data); setInquiries(i.data); setApps(a.data); setBlogs(b.data);
        setTsInq(ti.data.data); setTsApp(ta.data.data);
        setTopCats(cats.data); setStatusBreakdown(sts.data); setSeo(rdy.data);
        setAttention(att.data); setHealth(h.data);
      } catch {}
    })();
  }, []);

  const totalAttention = attention.reduce((acc, a) => acc + a.count, 0);

  return (
    <AdminLayout>
      <PageHeader title="Dashboard" subtitle="Overview of site content, activity and search-readiness." />
      <div className="p-8 space-y-8" data-testid="dashboard-content">

        {/* KPI grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {kpiTiles.map((t) => {
            const Icon = t.icon;
            const value = stats[t.key] ?? 0;
            const secondary = t.suffix ? stats[`${t.key}${t.suffix}`] : (t.secondaryKey ? stats[t.secondaryKey] : null);
            const secondaryLabel = t.suffix ? "active" : t.secondaryLabel;
            return (
              <Link to={t.link} key={t.key} data-testid={`stat-${t.key}`}
                className="group bg-white border border-[#E2E8F0] rounded-lg p-5 hover:border-[#C2410C] transition-colors duration-200">
                <div className="flex items-start justify-between">
                  <Icon size={20} weight="duotone" style={{ color: t.color }} />
                  <ArrowRight size={14} className="text-[#9CA3AF] group-hover:text-[#C2410C]" weight="bold" />
                </div>
                <div className="mt-4 text-3xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>{value}</div>
                <div className="mt-0.5 text-xs text-[#4B5563]">{t.label}</div>
                {secondary !== null && secondary !== undefined && (
                  <div className="mt-2 pt-2 border-t border-[#E2E8F0] text-[10px] text-[#4B5563] uppercase tracking-[0.18em]">
                    {secondary} {secondaryLabel}
                  </div>
                )}
              </Link>
            );
          })}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* SEO readiness */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="seo-readiness">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs tracking-[0.2em] uppercase text-[#4B5563] font-semibold">Search readiness</div>
              <Gauge size={18} weight="duotone" className="text-[#C2410C]" />
            </div>
            <div className="text-4xl font-bold text-[#1F2937] mt-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {seo ? `${seo.percent}%` : "—"}
            </div>
            <div className="text-xs text-[#4B5563] mt-1">
              {seo ? `${seo.ready} of ${seo.total} products SEO-ready` : "Loading…"}
            </div>
            <div className="mt-4 h-2 w-full bg-[#F1F5F9] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${seo?.percent || 0}%`, background: `linear-gradient(90deg, ${SAFFRON} 0%, ${SKY} 100%)` }} />
            </div>
            {seo?.checks && (
              <ul className="mt-4 space-y-2 text-xs">
                {Object.entries(seo.checks).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between">
                    <span className="text-[#4B5563] capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="font-medium text-[#1F2937]">{v.percent}%</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Inquiry status donut */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="inquiry-breakdown">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs tracking-[0.2em] uppercase text-[#4B5563] font-semibold">Inquiry status</div>
              <ChatCircleText size={18} weight="duotone" className="text-[#0EA5E9]" />
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusBreakdown} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {statusBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend iconSize={8} iconType="circle" wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Top categories */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="top-categories">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs tracking-[0.2em] uppercase text-[#4B5563] font-semibold">Top product categories</div>
              <Tag size={18} weight="duotone" className="text-[#C2410C]" />
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topCats} layout="vertical" margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11, fill: "#4B5563" }} />
                <Tooltip />
                <Bar dataKey="count" fill={SAFFRON} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Time series */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TsChart title="Inquiries · last 90 days" data={tsInq} color={SAFFRON} testId="ts-inquiries" />
          <TsChart title="Applications · last 90 days" data={tsApp} color={SKY} testId="ts-applications" />
        </div>

        {/* Needs attention + Health + Quick links */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 xl:col-span-2" data-testid="needs-attention">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs tracking-[0.2em] uppercase text-[#4B5563] font-semibold flex items-center gap-2">
                <Warning size={16} weight="duotone" className="text-[#C2410C]" /> Needs attention
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded bg-[#C2410C]/10 text-[#C2410C]">
                {totalAttention} items
              </span>
            </div>
            <ul className="divide-y divide-[#E2E8F0]">
              {attention.map((a) => (
                <li key={a.key} className="py-3 flex items-center justify-between gap-4">
                  <Link to={a.link} className="flex-1 min-w-0 text-sm text-[#1F2937] hover:text-[#C2410C]">
                    {a.label}
                  </Link>
                  <span className={`text-xs font-bold ${a.count > 0 ? "text-[#C2410C]" : "text-[#9CA3AF]"}`}>{a.count}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="health">
            <div className="text-xs tracking-[0.2em] uppercase text-[#4B5563] font-semibold mb-4">Site health</div>
            <ul className="space-y-3">
              {health.map((h) => (
                <li key={h.key} className="flex items-center justify-between">
                  <Link to={h.link} className="text-sm text-[#1F2937] hover:text-[#C2410C]">{h.label}</Link>
                  {h.ok
                    ? <CheckCircle size={18} weight="fill" className="text-[#0EA5E9]" />
                    : <Warning size={18} weight="fill" className="text-[#C2410C]" />}
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-5 border-t border-[#E2E8F0]">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#4B5563] font-semibold mb-2 flex items-center gap-1">
                <Sparkle size={12} /> Quick actions
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { to: "/admin/products", label: "+ Product" },
                  { to: "/admin/blogs", label: "+ Blog" },
                  { to: "/admin/careers", label: "+ Role" },
                  { to: "/admin/faqs", label: "+ FAQ" },
                  { to: "/admin/redirects", label: "Redirects" },
                ].map((q) => (
                  <Link key={q.to} to={q.to} className="text-xs px-3 py-1.5 rounded-full bg-[#F1F5F9] hover:bg-[#C2410C] hover:text-white text-[#4B5563] transition-colors duration-200">
                    {q.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recent activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <RecentList title="Recent inquiries" link="/admin/inquiries" items={inquiries} renderItem={(it) => (
            <>
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#1F2937] truncate">{it.name}</div>
                <div className="text-xs text-[#4B5563] truncate">{it.subject || it.email}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${it.status === "new" ? "bg-[#C2410C]/10 text-[#C2410C]" : "bg-[#F1F5F9] text-[#4B5563]"}`}>
                {it.status}
              </span>
            </>
          )} testId="recent-inquiries" />

          <RecentList title="Recent applications" link="/admin/applications" items={apps} renderItem={(it) => (
            <>
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#1F2937] truncate">{it.name}</div>
                <div className="text-xs text-[#4B5563] truncate">{it.job_title}</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded bg-[#0EA5E9]/10 text-[#0369A1]">
                {shortDate(it.created_at)}
              </span>
            </>
          )} testId="recent-applications" />

          <RecentList title="Latest blogs" link="/admin/blogs" items={blogs} renderItem={(it) => (
            <>
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#1F2937] truncate">{it.title}</div>
                <div className="text-xs text-[#4B5563] truncate">{it.author || "—"}</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded bg-[#F1F5F9] text-[#4B5563]">
                {shortDate(it.created_at)}
              </span>
            </>
          )} testId="recent-blogs" />
        </div>
      </div>
    </AdminLayout>
  );
}

function TsChart({ title, data, color, testId }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid={testId}>
      <div className="text-xs tracking-[0.2em] uppercase text-[#4B5563] font-semibold mb-3">{title}</div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9CA3AF" }} tickFormatter={(d) => new Date(d).getDate() === 1 ? new Date(d).toLocaleDateString(undefined, { month: "short" }) : ""} interval={0} />
          <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} allowDecimals={false} />
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="count" stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function RecentList({ title, link, items, renderItem, testId }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg" data-testid={testId}>
      <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
        <div className="text-sm font-semibold text-[#1F2937]">{title}</div>
        <Link to={link} className="text-xs text-[#C2410C] hover:underline">View all</Link>
      </div>
      <ul className="divide-y divide-[#E2E8F0]">
        {items.length === 0 && <li className="px-6 py-8 text-sm text-[#4B5563] text-center">No items yet.</li>}
        {items.map((it) => (
          <li key={it.id} className="px-6 py-3 flex items-center justify-between gap-4">
            {renderItem(it)}
          </li>
        ))}
      </ul>
    </div>
  );
}
